import { IVec3 } from '@stair-craft/common/util';
import { BufferGeometry, Float32BufferAttribute, Vector3 } from 'three';

import { WOOD_TILE_MM } from './stair-board-geometry';

/** A handrail piece shorter than this in plan runs straight up a post (mm). */
const UPRIGHT_PLAN: number = 1;

/** The geometry user-data key holding how many runs a swept handrail has; the joint check reads it. */
export const HANDRAIL_SWEEP_RUNS: string = 'handrailSweepRuns';

/** The triangles of one run between two joints: two for each of the four side faces, first in the buffer. */
export const SWEEP_RUN_TRIANGLES: number = 8;

/** The corner signs of the rail section, going round it: across the rail, then up it. */
const CORNERS: readonly (readonly [number, number])[] = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
];

/** The two section axes of one straight piece: across it in plan, and up it square to its run. */
interface IPieceFrame {
    readonly across: Vector3;
    readonly up: Vector3;
}

function vec(point: IVec3): Vector3 {
    return new Vector3(point.x, point.y, point.z);
}

function planLength(from: IVec3, to: IVec3): number {
    return Math.hypot(to.x - from.x, to.z - from.z);
}

/** The section axes of a piece: `across` is level and square to its plan run, `up` is square to the piece, pointing up. */
function pieceFrame(from: IVec3, to: IVec3): IPieceFrame {
    const run: Vector3 = vec(to).sub(vec(from));
    const across: Vector3 = new Vector3(run.z, 0, -run.x).normalize();
    const up: Vector3 = new Vector3().crossVectors(run, across).normalize();
    return { across, up: up.y < 0 ? up.negate() : up };
}

/** The mitre of two unit axes: the bisector stretched so the rail keeps its size on both sides of the joint. */
function mitre(a: Vector3, b: Vector3): Vector3 {
    return a
        .clone()
        .add(b)
        .multiplyScalar(1 / Math.max(0.2, 1 + a.dot(b)));
}

/** Whether a chain is one piece straight up a post: the sweep has no plan direction for it. */
export function isUprightChain(chain: readonly IVec3[]): boolean {
    return chain.length === 2 && planLength(chain[0], chain[1]) < UPRIGHT_PLAN;
}

/**
 * The handrail cut into chains a sweep can follow: a piece that runs straight up a post has no
 * plan direction and stays a chain of its own two points.
 */
export function handrailChains(points: readonly IVec3[]): IVec3[][] {
    const chains: IVec3[][] = [];
    let chain: IVec3[] = [];
    for (let i: number = 0; i < points.length - 1; i++) {
        if (planLength(points[i], points[i + 1]) < UPRIGHT_PLAN) {
            chains.push(...(chain.length > 1 ? [chain] : []), [points[i], points[i + 1]]);
            chain = [];
            continue;
        }
        chain = chain.length === 0 ? [points[i], points[i + 1]] : [...chain, points[i + 1]];
    }
    return chain.length > 1 ? [...chains, chain] : chains;
}

/**
 * One solid handrail along a chain of points: the rail section swept along the chain with mitred
 * joints, so a handrail that bends in short pieces over the winders shows no corner of one piece
 * standing out of the next. The section stands as a box handrail piece does: its sides are
 * vertical, its top and bottom square to the run. The grain runs along the rail. Every triangle
 * faces away from the rail axis.
 */
export function buildHandrailSweepGeometry(chain: readonly IVec3[], width: number, height: number): BufferGeometry {
    const frames: IPieceFrame[] = chain.slice(1).map((to: IVec3, i: number) => pieceFrame(chain[i], to));
    const sections: Vector3[][] = chain.map((point: IVec3, i: number) => {
        const before: IPieceFrame = frames[Math.max(0, i - 1)];
        const after: IPieceFrame = frames[Math.min(frames.length - 1, i)];
        const across: Vector3 = mitre(before.across, after.across);
        const up: Vector3 = mitre(before.up, after.up);
        return CORNERS.map(([side, rise]: readonly [number, number]) =>
            vec(point)
                .addScaledVector(across, (side * width) / 2)
                .addScaledVector(up, (rise * height) / 2)
        );
    });
    const along: number[] = [0];
    for (let i: number = 1; i < chain.length; i++) {
        along.push(along[i - 1] + vec(chain[i]).distanceTo(vec(chain[i - 1])));
    }

    const positions: number[] = [];
    const uvs: number[] = [];
    const triangle: (corners: readonly Vector3[], inside: Vector3, uv: readonly number[]) => void = (
        corners: readonly Vector3[],
        inside: Vector3,
        uv: readonly number[]
    ): void => {
        const [a, b, c]: readonly Vector3[] = corners;
        const normal: Vector3 = new Vector3().crossVectors(b.clone().sub(a), c.clone().sub(a));
        const order: number[] = normal.dot(a.clone().sub(inside)) >= 0 ? [0, 1, 2] : [0, 2, 1];
        for (const k of order) {
            positions.push(corners[k].x, corners[k].y, corners[k].z);
            uvs.push(uv[2 * k], uv[2 * k + 1]);
        }
    };

    for (let i: number = 0; i < chain.length - 1; i++) {
        const middle: Vector3 = vec(chain[i]).lerp(vec(chain[i + 1]), 0.5);
        const u0: number = along[i] / WOOD_TILE_MM;
        const u1: number = along[i + 1] / WOOD_TILE_MM;
        for (let k: number = 0; k < CORNERS.length; k++) {
            const next: number = (k + 1) % CORNERS.length;
            const v1: number = (k % 2 === 0 ? width : height) / WOOD_TILE_MM;
            const [a, b, c, d]: Vector3[] = [sections[i][k], sections[i][next], sections[i + 1][next], sections[i + 1][k]];
            triangle([a, b, c], middle, [u0, 0, u0, v1, u1, v1]);
            triangle([a, c, d], middle, [u0, 0, u1, v1, u1, 0]);
        }
    }
    const capU: number = width / WOOD_TILE_MM;
    const capV: number = height / WOOD_TILE_MM;
    const ends: [number, number][] = [
        [0, 1],
        [chain.length - 1, chain.length - 2],
    ];
    for (const [end, toward] of ends) {
        const [a, b, c, d]: Vector3[] = sections[end];
        const inside: Vector3 = vec(chain[end]).lerp(vec(chain[toward]), 0.5);
        triangle([a, b, c], inside, [0, 0, capU, 0, capU, capV]);
        triangle([a, c, d], inside, [0, 0, capU, capV, 0, capV]);
    }

    const geometry: BufferGeometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geometry.computeVertexNormals();
    geometry.userData[HANDRAIL_SWEEP_RUNS] = chain.length - 1;
    return geometry;
}
